import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);
const STORAGE_KEY = 'scribeshadow:theme-mode';

const getInitialMode = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (_error) {
    // ignore storage failures (e.g. privacy mode)
  }

  if (typeof window !== 'undefined' && window.matchMedia) {
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch (_error) {
      // ignore matchMedia failures
    }
  }

  return 'light';
};

export const ThemeProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => getInitialMode());

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const body = document.body;
    body.classList.remove('theme-light', 'theme-dark');
    body.classList.add(mode === 'dark' ? 'theme-dark' : 'theme-light');

    const root = document.documentElement;
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(mode === 'dark' ? 'theme-dark' : 'theme-light');
    root.style.colorScheme = mode;

    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (_error) {
      // ignore storage failures
    }
  }, [mode]);

  const setMode = useCallback((nextMode: ThemeMode) => {
    setModeState(nextMode);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const contextValue = useMemo<ThemeContextValue>(() => ({
    mode,
    isDark: mode === 'dark',
    toggleMode,
    setMode,
  }), [mode, setMode, toggleMode]);

  const algorithm = mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;
  const isDark = mode === 'dark';

  const surfaceBase = isDark ? '#1f2430' : '#ffffff';
  const surfaceElevated = isDark ? '#1f2430' : '#ffffff';
  const surfaceMuted = isDark ? '#1b2029' : '#eef2f7';
  const controlBg = isDark ? '#11151c' : '#ffffff';
  const textPrimary = isDark ? '#e7ecf5' : '#1f2933';
  const textSecondary = isDark ? '#b8c3dc' : '#475569';
  const borderSubtle = isDark ? 'rgba(184, 195, 220, 0.38)' : 'rgba(15, 23, 42, 0.14)';
  const accentPrimary = isDark ? '#69b1ff' : '#1d4ed8';
  const accentPrimaryHover = isDark ? '#8cceff' : '#1e40af';

  const baseToken = useMemo(() => ({
    colorPrimary: accentPrimary,
    colorInfo: isDark ? '#60a5fa' : '#1d4ed8',
    colorSuccess: isDark ? '#4ade80' : '#15803d',
    colorWarning: isDark ? '#fcd34d' : '#b45309',
    colorError: isDark ? '#f87171' : '#b42318',
    colorBgBase: isDark ? '#0f1115' : '#f5f7fa',
    colorBgContainer: surfaceBase,
    colorBgElevated: surfaceElevated,
    colorBgLayout: isDark ? '#0f1115' : '#f5f7fa',
    colorFillAlter: surfaceMuted,
    colorFillSecondary: isDark ? 'rgba(184, 195, 220, 0.16)' : '#eef2f7',
    colorFillTertiary: isDark ? 'rgba(184, 195, 220, 0.10)' : '#f8fafc',
    colorBorder: borderSubtle,
    colorBorderSecondary: borderSubtle,
    colorText: textPrimary,
    colorTextBase: textPrimary,
    colorTextHeading: textPrimary,
    colorTextLabel: textPrimary,
    colorTextDescription: textSecondary,
    colorTextSecondary: textSecondary,
    colorTextTertiary: textSecondary,
    colorTextQuaternary: isDark ? 'rgba(184, 195, 220, 0.68)' : '#94a3b8',
    colorLink: accentPrimary,
    colorLinkHover: accentPrimaryHover,
    colorPrimaryHover: accentPrimaryHover,
    controlOutline: isDark ? 'rgba(105, 177, 255, 0.34)' : 'rgba(29, 78, 216, 0.24)',
  }), [
    accentPrimary,
    accentPrimaryHover,
    borderSubtle,
    isDark,
    surfaceBase,
    surfaceElevated,
    surfaceMuted,
    textPrimary,
    textSecondary,
  ]);

  const componentThemes = useMemo(() => ({
    Alert: {
      colorText: textPrimary,
      colorTextHeading: textPrimary,
      colorTextDescription: textSecondary,
    },
    Card: {
      colorBgContainer: surfaceBase,
      colorBorderSecondary: borderSubtle,
      colorText: textPrimary,
      colorTextHeading: textPrimary,
    },
    Form: {
      colorTextLabel: textPrimary,
      colorTextDescription: textSecondary,
    },
    Input: {
      colorBgContainer: controlBg,
      colorBorder: borderSubtle,
      colorText: textPrimary,
      colorTextPlaceholder: textSecondary,
      activeBorderColor: accentPrimary,
      hoverBorderColor: accentPrimary,
    },
    Modal: {
      colorBgElevated: surfaceElevated,
      colorText: textPrimary,
      colorTextHeading: textPrimary,
    },
    Message: {
      colorBgElevated: surfaceElevated,
      colorText: textPrimary,
    },
    Notification: {
      colorBgElevated: surfaceElevated,
      colorText: textPrimary,
    },
    Select: {
      colorBgContainer: controlBg,
      colorBgElevated: surfaceElevated,
      colorBorder: borderSubtle,
      colorText: textPrimary,
      colorTextPlaceholder: textSecondary,
      optionSelectedColor: textPrimary,
      optionSelectedBg: isDark ? 'rgba(105, 177, 255, 0.24)' : '#eff6ff',
      optionActiveBg: isDark ? 'rgba(105, 177, 255, 0.16)' : '#eef2f7',
    },
    Segmented: {
      itemColor: textSecondary,
      itemHoverColor: textPrimary,
      itemSelectedColor: textPrimary,
      itemSelectedBg: isDark ? 'rgba(105, 177, 255, 0.22)' : '#ffffff',
      trackBg: surfaceMuted,
    },
    Table: {
      colorBgContainer: surfaceBase,
      colorFillAlter: surfaceMuted,
      colorText: textPrimary,
      colorTextHeading: textPrimary,
      borderColor: borderSubtle,
      headerBg: surfaceMuted,
      headerColor: textPrimary,
      rowHoverBg: isDark ? 'rgba(105, 177, 255, 0.14)' : '#eef2f7',
    },
    Tabs: {
      itemColor: textSecondary,
      itemHoverColor: accentPrimaryHover,
      itemSelectedColor: accentPrimary,
      inkBarColor: accentPrimary,
    },
    Tooltip: {
      colorBgSpotlight: isDark ? '#273244' : 'rgba(0, 0, 0, 0.85)',
      colorTextLightSolid: '#ffffff',
    },
  }), [
    accentPrimary,
    accentPrimaryHover,
    borderSubtle,
    controlBg,
    isDark,
    surfaceBase,
    surfaceElevated,
    surfaceMuted,
    textPrimary,
    textSecondary,
  ]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider
        theme={{
          algorithm,
          token: baseToken,
          components: componentThemes
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
